import React from 'react';
import { Navigate } from 'react-router-dom';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useCustomer } from '../contexts/CustomerContext';
import { RoleName, hasAnyRole, hasMinimumRoleLevel, getUserRoleCategory, RoleCategory } from '../config/roles';
import { AlertCircle } from 'lucide-react';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredRoles?: RoleName[];
  minRoleLevel?: number;
  allowedCategories?: RoleCategory[];
  redirectTo?: string;
  showAccessDenied?: boolean;
}

/**
 * Component to protect routes based on user roles
 */
export const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  requiredRoles,
  minRoleLevel,
  allowedCategories,
  redirectTo = '/dashboard',
  showAccessDenied = true,
}) => {
  const { roles: globalRoles, authenticated, initialized, getRolesForCustomer } = useKeycloak();
  const { selectedCustomer } = useCustomer();

  // Get selected customer ID from context (not localStorage)
  const selectedCustomerId = selectedCustomer?.id || null;

  // Use customer-specific roles if a customer is selected, otherwise use global roles
  const roles = selectedCustomerId ? getRolesForCustomer(selectedCustomerId) : globalRoles;

  // DEBUG: Log what roles RoleBasedRoute is using
  console.log('[RoleBasedRoute] Permission Check:', {
    selectedCustomerId,
    selectedCustomerName: selectedCustomer?.name,
    roles,
    requiredRoles,
    allowedCategories,
    userCategory: getUserRoleCategory(roles)
  });

  // Wait for initialization
  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Determine access based on provided constraints
  // If both requiredRoles and allowedCategories are specified, use OR logic (pass if either matches)
  // If only one is specified, use that constraint alone

  const hasRequiredRolesCheck = requiredRoles && requiredRoles.length > 0;
  const hasAllowedCategoriesCheck = allowedCategories && allowedCategories.length > 0;
  const hasMinRoleLevelCheck = minRoleLevel !== undefined;

  // Check role requirements
  const passesRequiredRoles = hasRequiredRolesCheck ? hasAnyRole(roles, requiredRoles) : false;
  const userCategory = getUserRoleCategory(roles);
  const passesAllowedCategories = hasAllowedCategoriesCheck ? (userCategory && allowedCategories.includes(userCategory)) : false;
  const passesMinRoleLevel = hasMinRoleLevelCheck ? hasMinimumRoleLevel(roles, minRoleLevel) : true;

  // If both requiredRoles and allowedCategories are specified, pass if EITHER matches (OR logic)
  // This allows specific roles (like 'administrador') to access routes even if their category isn't listed
  if (hasRequiredRolesCheck && hasAllowedCategoriesCheck) {
    if (!passesRequiredRoles && !passesAllowedCategories) {
      if (showAccessDenied) {
        return <AccessDenied />;
      }
      return <Navigate to={redirectTo} replace />;
    }
  } else {
    // If only requiredRoles is specified
    if (hasRequiredRolesCheck && !passesRequiredRoles) {
      if (showAccessDenied) {
        return <AccessDenied />;
      }
      return <Navigate to={redirectTo} replace />;
    }

    // If only allowedCategories is specified
    if (hasAllowedCategoriesCheck && !passesAllowedCategories) {
      if (showAccessDenied) {
        return <AccessDenied />;
      }
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check minimum role level (always AND logic)
  if (!passesMinRoleLevel) {
    if (showAccessDenied) {
      return <AccessDenied />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // User has access
  return <>{children}</>;
};

/**
 * Access Denied component
 */
const AccessDenied: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
          <AlertCircle className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Acesso Negado</h1>
        <p className="text-gray-600 mb-6">
          Você não tem permissão para acessar esta página. Entre em contato com o administrador se precisar de acesso.
        </p>
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
        >
          Voltar
        </button>
      </div>
    </div>
  );
};

export default RoleBasedRoute;
